<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import { EmptyState, PageHero } from '~/components';
import {
  AppSkeletonLoader,
  AppTextInput,
  AppTypography,
} from '~/components/base';
import {
  CampaignCard,
  CampaignDetailModal,
  CampaignFilterModal,
  CampaignSortModal,
} from '~/components/campaigns';
import { LocationPickerModal } from '~/components/layout';
import { useCampaigns } from '~/composables/useCampaigns';
import { useQuerySettling } from '~/composables/useQuerySettling';
import { useLocationStore } from '~/stores/location';

definePageMeta({ layout: 'default' });
useHead({ title: 'Campaigns' });
useSeoMeta({
  description:
    'Explore candidate-led and party-led campaigns in the 2026 Victorian State Election.',
  ogTitle: 'Campaigns | VoteWise',
  ogDescription:
    'Explore candidate-led and party-led campaigns in the 2026 Victorian State Election.',
});

type SortOrder = 'latest' | 'oldest';
type TypeFilter = 'all' | 'community' | 'party';

const query = ref('');
const sortOrder = ref<SortOrder>('latest');
const typeFilter = ref<TypeFilter>('all');

const location = useLocationStore();
const locationModalVisible = ref(false);
const sortModalVisible = ref(false);
const filterModalVisible = ref(false);

const route = useRoute();

// Desktop opens a campaign in a modal instead of navigating (the card emits
// `open` only at that breakpoint). Holding the id rather than a boolean lets
// the modal mount fresh per campaign, so its one-shot fetch runs for the one
// that was clicked.
//
// VTW-497: seeded from `?campaign=` so the popup is reachable by URL, which is
// what lets Back from a desktop campaign report land on this listing with the
// campaign reopened. Deliberately read once, on setup, and never written back:
// the default layout keys <NuxtPage> on route.fullPath, so pushing this param
// on every card click would remount the whole listing (and refetch it) mid
// interaction. The URL is an entry point here, not a mirror of local state.
const openCampaignId = ref<string | null>(
  typeof route.query.campaign === 'string' ? route.query.campaign : null,
);

// Chip labels come straight from the two Figma frames, which word them
// differently: the desktop toolbar says "Latest" / "All Campaigns", the mobile
// filter row says "Newest First" / "All". Neither matches the longer wording
// the modals use for their option rows.
const sortChipLabel = computed(() =>
  sortOrder.value === 'oldest' ? 'Oldest' : 'Latest',
);
const sortChipLabelMobile = computed(() =>
  sortOrder.value === 'oldest' ? 'Oldest First' : 'Newest First',
);

const typeChipLabel = computed(() => {
  if (typeFilter.value === 'community') return 'Candidate Led';
  if (typeFilter.value === 'party') return 'Party Led';
  return null;
});
const filterChipLabel = computed(() => typeChipLabel.value ?? 'All Campaigns');
const filterChipLabelMobile = computed(() => typeChipLabel.value ?? 'All');

// 8 per page fills the 4-column desktop grid exactly twice — the Figma frame
// shows two rows of four before "Show More".
const {
  campaigns: visibleCampaigns,
  hasMore,
  loadMore,
  pending,
  error,
} = useCampaigns({
  query,
  sortOrder,
  typeFilter,
  suburbId: () => location.location?.suburbId ?? null,
  perPage: 8,
});

const settling = useQuerySettling(query);

// No infinite scroll here: paging past the first page is an explicit "Show
// More" click. (The candidates list and the location picker still auto-load.)
</script>

<template>
  <div class="campaigns-page">
    <PageHero constrained max-width="max-w-[1200px]">
      <div class="campaigns-page__panel">
        <div
          class="campaigns-page__toolbar flex flex-col items-stretch gap-3 lg:flex-row lg:items-center lg:gap-4"
        >
          <AppTypography
            type="title-5"
            element="h1"
            class="order-1 hidden lg:mr-auto lg:block"
            >Campaigns</AppTypography
          >

          <!-- Figma puts the search field on the desktop toolbar too, to the
               right of the sort/filter chips; the location chip inside it
               stays mobile-only (desktop carries it in the navbar search). -->
          <div
            data-testid="campaigns-search"
            class="campaigns-page__search order-2 flex items-center gap-2 lg:order-3"
          >
            <Icon
              name="solar:magnifer-linear"
              class="campaigns-page__search-icon text-color-subtext shrink-0"
            />
            <AppTextInput
              v-model="query"
              placeholder="Search Campaigns"
              :floating-label="false"
              variant="outlined"
              size="small"
              enterkeyhint="search"
              class="campaigns-page__search-input"
            />
            <button
              type="button"
              data-testid="campaigns-location"
              class="campaigns-page__search-location flex max-w-[45%] shrink-0 items-center gap-1 rounded-full px-3 py-1.5 lg:hidden"
              @click="locationModalVisible = true"
            >
              <Icon
                name="solar:map-point-linear"
                size="16"
                class="campaigns-page__search-location-icon shrink-0"
              />
              <AppTypography type="caption" class="truncate">
                {{ location.displayLabel }}
              </AppTypography>
            </button>
          </div>

          <!-- One control at every breakpoint: chips that open the shared
               Sort/Filters modals. Desktop previously used inline AppSelect
               dropdowns, which matched neither the Figma chips nor the modal
               pattern the other listing pages use. -->
          <div
            class="campaigns-page__chips order-3 flex items-center gap-2 lg:order-2"
          >
            <!-- The two frames label these chips differently — mobile reads
                 "Newest First" / "All", desktop "Latest" / "All Campaigns".
                 Both labels are rendered and swapped by breakpoint rather than
                 measured in JS, so SSR emits the same markup either way. -->
            <button
              type="button"
              data-testid="campaigns-sort-trigger"
              class="campaigns-page__sort-trigger flex items-center gap-1"
              @click="sortModalVisible = true"
            >
              <Icon name="solar:sort-vertical-linear" class="shrink-0" />
              <span class="lg:hidden">{{ sortChipLabelMobile }}</span>
              <span class="hidden lg:inline">{{ sortChipLabel }}</span>
              <Icon name="solar:alt-arrow-down-bold" class="shrink-0" />
            </button>
            <button
              type="button"
              data-testid="campaigns-filter-trigger"
              class="campaigns-page__filter-trigger flex items-center gap-1"
              @click="filterModalVisible = true"
            >
              <span class="lg:hidden">{{ filterChipLabelMobile }}</span>
              <span class="hidden lg:inline">{{ filterChipLabel }}</span>
              <Icon name="solar:alt-arrow-down-bold" class="shrink-0" />
            </button>
          </div>
        </div>

        <div
          v-if="(pending || settling) && !visibleCampaigns.length"
          data-testid="campaigns-loading"
          class="campaigns-page__grid grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6"
        >
          <AppSkeletonLoader
            v-for="n in 4"
            :key="n"
            :items="[{ height: '160px' }]"
            container-class=""
          />
        </div>
        <EmptyState
          v-else-if="error && !visibleCampaigns.length"
          data-testid="campaigns-error-state"
          title="Couldn't load campaigns"
          illustration="result"
          description="Please try again."
        />
        <div
          v-else-if="visibleCampaigns.length"
          class="campaigns-page__grid grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4 lg:gap-6"
        >
          <CampaignCard
            v-for="campaign in visibleCampaigns"
            :key="campaign.id"
            :campaign="campaign"
            @open="openCampaignId = campaign.id"
          />
        </div>
        <!-- One verdict for every empty result, narrowed by a search/filter or
             not: the copy speaks to the area rather than the query. -->
        <EmptyState
          v-else-if="!pending && !settling"
          data-testid="campaigns-empty-state"
          title="No Campaigns Yet"
          illustration="campaigns"
          description="There are no campaigns to display in your area at the moment."
        />

        <div v-if="hasMore" class="campaigns-page__more flex justify-center">
          <!-- Figma renders this as an underlined brand-green text link, not
               the outlined pill AppButton gives — the same treatment as the
               "Learn More" link on the cards above it. -->
          <button
            type="button"
            data-testid="show-more-campaigns"
            class="campaigns-page__more-button"
            @click="loadMore"
          >
            <AppTypography type="subheadline-bold" class="underline"
              >Show More</AppTypography
            >
          </button>
        </div>
      </div>
    </PageHero>
    <LocationPickerModal
      v-if="locationModalVisible"
      v-model:visible="locationModalVisible"
    />
    <CampaignSortModal
      v-if="sortModalVisible"
      v-model:visible="sortModalVisible"
      v-model="sortOrder"
    />
    <CampaignFilterModal
      v-if="filterModalVisible"
      v-model:visible="filterModalVisible"
      v-model="typeFilter"
    />
    <CampaignDetailModal
      v-if="openCampaignId"
      :key="openCampaignId"
      :campaign-id="openCampaignId"
      :visible="true"
      @update:visible="openCampaignId = null"
    />
  </div>
</template>

<style scoped lang="scss">
.campaigns-page {
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
  min-height: calc(100vh - var(--header-height));

  &__panel {
    display: flex;
    flex-direction: column;
    gap: $spacing-xl;

    // Figma: the whole panel sits in a big white rounded card on desktop;
    // on mobile the toolbar/cards sit directly on the sand band, each card
    // carrying its own white background.
    @media (min-width: 1024px) {
      padding: $spacing-2xl;
      border-radius: $radius-2xl;
      background-color: $surface-color-bg;
    }
  }

  &__search {
    width: 100%;
    height: 40px;
    border: 1px solid $border-color;
    // Mobile is a rounded rectangle (radius/md); only the desktop toolbar
    // field is a full pill.
    border-radius: $radius-md;
    padding: 0 $spacing-sm 0 $spacing-md;
    background-color: $surface-color-bg;

    // Figma: 48px on mobile, matching the Candidates/Parties search field.
    @media (max-width: 639.98px) {
      height: 48px;
    }

    // Figma: ~343px on desktop (287px of text plus the search icon), and it
    // no longer stretches — the chips sit beside it on the same row.
    @media (min-width: 1024px) {
      width: 343px;
      flex-shrink: 0;
      border-radius: 9999px;
      padding: 0 $spacing-lg;
    }

    :deep(.app-floating-label-wrapper) {
      display: flex;
      align-items: center;
      width: 100%;
    }

    :deep(.p-inputtext) {
      width: 100%;
      height: 38px;
      border: none;
      background: transparent;
      box-shadow: none;
      padding: 0;

      &:focus,
      &:enabled:focus {
        border: none;
        box-shadow: none;
        outline: none;
      }

      // Figma: text/tag (#827b72) placeholder on mobile, not the theme's
      // default input placeholder grey — matches the Candidates page's
      // search field (VTW-543).
      @media (max-width: 639.98px) {
        &::placeholder {
          color: $text-color-tag;
        }
      }
    }
  }

  &__search-icon {
    font-size: 1.125rem;
  }

  // Matches the Candidates/Parties pages' mobile-only location trigger.
  &__search-location {
    border: 1px solid $surface-color-disabled;
    background-color: $surface-color-tag;
    color: $text-color-primary;
  }

  &__search-location-icon {
    color: $text-color-tag;
  }

  &__chips {
    flex-wrap: wrap;
  }

  // Both triggers open the shared modals at every breakpoint — desktop no
  // longer has its own inline dropdowns. Shared chip shape; the 32px height
  // is the mobile filter row, and desktop grows to 40px so the chips line up
  // with the search field sharing their row.
  &__sort-trigger,
  &__filter-trigger {
    height: 32px;
    padding: 0 $spacing-md;
    border-radius: 9999px;
    font: inherit;
    white-space: nowrap;
    cursor: pointer;

    @media (min-width: 1024px) {
      height: 40px;
      padding: 0 $spacing-lg;
    }
  }

  // "Fields / tags" pill — the same treatment in both frames.
  &__sort-trigger {
    border: 1px solid $border-color;
    background-color: $surface-color-tag;
    color: $text-color-primary;
  }

  // The odd one out: mobile paints the type filter as a solid dark-gold chip
  // (Figma complementary/darker-shade-01, no matching token here) to mark it as
  // the primary filter, while the desktop toolbar renders it as the same
  // neutral pill as Sort.
  &__filter-trigger {
    border: none;
    background-color: #766035;
    color: $text-color-white;
    font-weight: 600;

    @media (min-width: 1024px) {
      border: 1px solid $border-color;
      background-color: $surface-color-tag;
      color: $text-color-primary;
      font-weight: inherit;
    }
  }

  &__more {
    margin-top: $spacing-md;
  }

  &__more-button {
    padding: $spacing-sm 0;
    border: none;
    background: transparent;
    color: $text-color-brand;
    cursor: pointer;
  }
}
</style>
